/**
 * MasterViolations — قائمة مخالفات الخدمات (central list)
 * Route: /control/asb606-admin/master-violations
 * Each violation = its own block: name AR/EN (AI translate), calculation mode, amount, start fee, cap, grace days.
 * Services pick from this list per emirate and may customise the values (see EmirateViolationsSection).
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, Edit3, Loader2, Plus, Power, Sparkles, Trash2 } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import { useLanguage } from '../context/LanguageContext';
import { getAuthHeaders } from '../utils/authHeaders';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import ViolationValuesFields from '../components/services/ViolationValuesFields';
import { computeFine, modeLabel } from '../components/services/violationCalc';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEFAULT_VALUES = { mode: 'daily', amount: '', start_fee: null, max_fee: null, grace_days: 30 };

export default function MasterViolations() {
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = useCallback((ar, en) => (isAr ? ar : en), [isAr]);
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {} | violation
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/master-violations`, { headers: getAuthHeaders() });
      setItems(r.data?.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleActive = async (v) => {
    try {
      await axios.put(`${API}/master-violations/${v.id}`, { ...v, is_active: !v.is_active }, { headers: getAuthHeaders() });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  const remove = async () => {
    const v = toDelete; setToDelete(null);
    try {
      await axios.delete(`${API}/master-violations/${v.id}`, { headers: getAuthHeaders() });
      toast.success(t('تم حذف المخالفة', 'Violation deleted')); load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  return (
    <DashboardLayout
      title="قائمة مخالفات الخدمات" titleEn="Service Violations List"
      subtitle="القائمة المركزية — كل خدمة تختار منها مخالفاتها لكل إمارة وتعدّل قيمها" subtitleEn="Central list — each service picks its violations per emirate and may customise the values"
      screenCode="CPL-009"
      backPath="/control-panel-settings"
      breadcrumb={[
        { labelAr: 'التحكم والمعلومات للمنصات', labelEn: 'Platform Control & Info', path: '/dashboard?view=platform-control' },
        { labelAr: 'ASB606 ADMIN (للتحكم)', labelEn: 'ASB606 ADMIN (Control)', path: '/control-panel-settings' },
        { labelAr: 'قائمة مخالفات الخدمات', labelEn: 'Service Violations List' },
      ]}
    >
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="master-violations-page">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <p className="text-sm text-white/50 max-w-2xl">
            {t('كل مخالفة بلوك مستقل: الاسم بالعربي والإنجليزي، نوع الاحتساب (يومي أو مبلغ ثابت)، قيمة اليوم/المبلغ، بداية التسعيرة (اختياري)، السقف (اختياري)، ومهلة المخالفة. طريقة الحساب: الأيام المتأخرة − المهلة = الأيام المحتسبة، ثم رسم البداية + قيمة اليوم × الأيام المحتسبة، دون تجاوز السقف.',
               'Each violation is its own block: AR/EN name, calculation mode (daily or fixed), per-day value/amount, optional starting fee, optional cap and a grace period. Formula: days late − grace = counted days, then start fee + per-day × counted days, capped.')}
          </p>
          <Button onClick={() => setEditing({})} className="bg-rose-500 hover:bg-rose-600 text-white" data-testid="violation-add-btn">
            <Plus className="w-4 h-4 me-1" />{t('إضافة مخالفة', 'Add violation')}
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-16 text-white/40"><Loader2 className="w-7 h-7 animate-spin" /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 rounded-2xl border border-dashed border-white/10 text-white/40" data-testid="violations-empty">
            <AlertTriangle className="w-8 h-8 mx-auto mb-2 opacity-40" />
            {t('لا توجد مخالفات بعد — أضف أول مخالفة مثل «تأخير تجديد الهوية الإماراتية»', 'No violations yet — add the first one, e.g. "Late Emirates ID renewal"')}
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((v) => (
              <div key={v.id} className={`rounded-2xl border p-4 flex flex-wrap items-center gap-4 ${v.is_active ? 'border-white/10 bg-white/[0.02]' : 'border-white/5 bg-white/[0.01] opacity-60'}`} data-testid={`violation-row-${v.id}`}>
                <div className="w-11 h-11 rounded-xl bg-rose-500/10 border border-rose-500/30 flex items-center justify-center shrink-0"><AlertTriangle className="w-5 h-5 text-rose-300" /></div>
                <div className="min-w-[200px] flex-1">
                  <div className="text-white font-bold">{v.name_ar} <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/10 text-white/60 ms-1">{modeLabel(v.mode, isAr)}</span></div>
                  <div className="text-white/50 text-xs" dir="ltr">{v.name_en}</div>
                  <div className="text-xs text-white/70 mt-1" data-testid={`violation-summary-${v.id}`}>
                    {v.mode === 'daily' ? `${Number(v.amount || 0).toLocaleString()} ${t('د.إ/يوم', 'AED/day')}` : `${Number(v.amount || 0).toLocaleString()} ${t('د.إ', 'AED')}`}
                    {v.start_fee !== null && v.start_fee !== undefined && ` · ${t('بداية', 'start')} ${Number(v.start_fee).toLocaleString()}`}
                    {v.max_fee !== null && v.max_fee !== undefined && ` · ${t('سقف', 'cap')} ${Number(v.max_fee).toLocaleString()}`}
                    {` · ${t('مهلة', 'grace')} ${Number(v.grace_days || 0)} ${t('يوم', 'days')}`}
                    <span className="text-[#c9a227]"> · {t('مثال 3 أيام بعد المهلة', 'e.g. 3 days after grace')}: {computeFine(v, Number(v.grace_days || 0) + 3).toLocaleString()} {t('د.إ', 'AED')}</span>
                  </div>
                  {v.usage > 0 && <div className="text-[11px] text-white/40 mt-0.5">{t(`مستخدمة في ${v.usage} خدمة/إمارة`, `Used in ${v.usage} service/emirate combos`)}</div>}
                </div>
                <div className="flex items-center gap-1.5">
                  <button onClick={() => toggleActive(v)} className={`h-9 px-3 rounded-lg border text-xs flex items-center gap-1 ${v.is_active ? 'border-emerald-500/40 text-emerald-300 bg-emerald-500/10' : 'border-white/10 text-white/50'}`} data-testid={`violation-toggle-${v.id}`}>
                    <Power className="w-3.5 h-3.5" />{v.is_active ? t('مفعّلة', 'Active') : t('متوقفة', 'Off')}
                  </button>
                  <button onClick={() => setEditing(v)} className="h-9 w-9 rounded-lg bg-white/5 border border-white/10 text-white/70 hover:bg-white/10 flex items-center justify-center" data-testid={`violation-edit-${v.id}`}><Edit3 className="w-4 h-4" /></button>
                  <button onClick={() => setToDelete(v)} className="h-9 w-9 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 hover:bg-red-500/20 flex items-center justify-center" data-testid={`violation-delete-${v.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-8 text-xs text-white/40">
          {t('لتطبيق المخالفات على خدمة: افتح الخدمة ← إدارة الخدمة ← بطاقة الإمارة ← «مخالفات الخدمة».', 'To apply violations to a service: open the service → Manage → emirate card → "Service violations".')}
          <button onClick={() => navigate('/control/cloudgm/visa-id')} className="ms-2 underline text-[#c9a227]" data-testid="go-to-visa-services">{t('خدمات التأشيرة والهوية', 'Visa & ID services')}</button>
        </div>
      </main>

      {editing !== null && <ViolationDialog v={editing} t={t} language={language} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="bg-[#1a1a2e] border-[#2a2a4e] text-white" data-testid="violation-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('حذف المخالفة؟', 'Delete violation?')}</AlertDialogTitle>
            <AlertDialogDescription className="text-white/70">
              {toDelete && t(`سيتم حذف «${toDelete.name_ar}» من القائمة المركزية ومن كل الخدمات والإمارات التي تستخدمها (${toDelete.usage || 0}).`, `"${toDelete.name_en}" will be removed from the master list and from every service/emirate using it (${toDelete.usage || 0}).`)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel className="border-[#2a2a4e] bg-transparent text-white">{t('إلغاء', 'Cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-700 text-white" data-testid="violation-delete-confirm">{t('نعم، احذف', 'Yes, delete')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}

function ViolationDialog({ v, t, language, onClose, onSaved }) {
  const isEdit = Boolean(v?.id);
  const [nameAr, setNameAr] = useState(v?.name_ar || '');
  const [nameEn, setNameEn] = useState(v?.name_en || '');
  const [values, setValues] = useState({ ...DEFAULT_VALUES, ...(v?.id ? { mode: v.mode, amount: v.amount, start_fee: v.start_fee, max_fee: v.max_fee, grace_days: v.grace_days } : {}) });
  const [translating, setTranslating] = useState(false);
  const [saving, setSaving] = useState(false);
  const canSave = nameAr.trim() && nameEn.trim() && Number(values.amount) > 0 && !saving;

  const translate = async () => {
    if (!nameAr.trim()) { toast.error(t('اكتب الاسم بالعربي أولاً', 'Enter the Arabic name first')); return; }
    setTranslating(true);
    try {
      const r = await axios.post(`${API}/translate`, { text: nameAr.trim(), source: 'ar', target: 'en' }, { headers: getAuthHeaders() });
      if (r.data?.translated) setNameEn(r.data.translated);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setTranslating(false); }
  };
  const save = async () => {
    setSaving(true);
    try {
      const body = {
        name_ar: nameAr.trim(), name_en: nameEn.trim(), mode: values.mode, amount: Number(values.amount || 0),
        start_fee: values.start_fee === null || values.start_fee === '' ? null : Number(values.start_fee),
        max_fee: values.max_fee === null || values.max_fee === '' ? null : Number(values.max_fee),
        grace_days: Number(values.grace_days || 0), is_active: v?.is_active ?? true, order: v?.order ?? 0,
      };
      if (isEdit) await axios.put(`${API}/master-violations/${v.id}`, body, { headers: getAuthHeaders() });
      else await axios.post(`${API}/master-violations`, body, { headers: getAuthHeaders() });
      toast.success(t('تم الحفظ', 'Saved')); onSaved();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(Array.isArray(d) ? d.map((x) => x.msg).join(' • ') : (d || e.message));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0b0b14] border-white/10 text-white sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="violation-dialog">
        <DialogHeader><DialogTitle>{isEdit ? t('تعديل مخالفة', 'Edit violation') : t('إضافة مخالفة', 'Add violation')}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-xs text-white/60">{t('نوع المخالفة (عربي) *', 'Violation type (Arabic) *')}</label>
            <Input value={nameAr} onChange={(e) => setNameAr(e.target.value)} placeholder={t('مثال: تأخير تجديد الهوية الإماراتية', 'e.g. تأخير تجديد الهوية')} className="bg-white/5 border-white/10 mt-1" data-testid="violation-name-ar" />
          </div>
          <div>
            <label className="text-xs text-white/60">{t('نوع المخالفة (إنجليزي) *', 'Violation type (English) *')}</label>
            <div className="flex gap-2 mt-1">
              <Input value={nameEn} onChange={(e) => setNameEn(e.target.value)} placeholder="e.g. Late Emirates ID renewal" dir="ltr" className="bg-white/5 border-white/10" data-testid="violation-name-en" />
              <Button type="button" variant="outline" onClick={translate} disabled={translating} className="border-[#d4af37]/40 text-[#d4af37] shrink-0" data-testid="violation-translate-btn">
                {translating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              </Button>
            </div>
          </div>
          <ViolationValuesFields values={values} onChange={setValues} language={language} testIdPrefix="violation-form" />
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="border-white/10 text-white/70">{t('إلغاء', 'Cancel')}</Button>
          <Button onClick={save} disabled={!canSave} className="bg-rose-500 hover:bg-rose-600 text-white" data-testid="violation-save-btn">
            {saving && <Loader2 className="w-4 h-4 animate-spin me-1" />}{isEdit ? t('حفظ التغييرات', 'Save changes') : t('إضافة', 'Add')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
